/**
 * Mobile-OK 본인확인 서비스
 * 드림시큐리티 Mobile-OK 표준창 연동
 *
 * MOKToken 방식: SDK → JS 콜백으로 mokToken 전달 → 백엔드 /result API 호출
 *
 * @see https://mobile-ok.com/guide/mok_std_guide/
 */

// 백엔드 API URL (SDK가 직접 호출하므로 항상 절대 URL 필요)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.ondoc.co.kr';

declare global {
  interface Window {
    MOBILEOK?: IMobileOK;
  }
}

interface IMobileOK {
  process: (
    requestUrl: string,
    deviceType: string,
    callbackFuncName: string,
  ) => void;
}

// 인증 요청 파라미터
export interface MobileOKRequestParams {
  serviceType?: 'telcoAuth' | 'telcoAuth-LMS'; // SMS 또는 LMS
  usageCode: string; // 이용목적 코드 (01001: 회원가입, 01007: 본인확인 등)
  userId?: number; // 사용자 ID
  returnUrl?: string; // 콜백 URL (구버전 호환용)
}

// 인증 결과 (백엔드에서 복호화 후 전달)
export interface MobileOKResult {
  resultCode: string; // 결과코드 (0000: 성공)
  resultMsg: string; // 결과메시지
  txId?: string; // 거래 ID
  ci?: string; // CI (연계정보)
  di?: string; // DI (중복가입확인정보)
  name?: string; // 성명
  birthDate?: string; // 생년월일 (YYYYMMDD)
  gender?: string; // 성별 (M/F)
  nationalInfo?: string; // 내외국인 (L: 내국인, F: 외국인)
  mobileNo?: string; // 휴대폰번호
  mobileCo?: string; // 통신사
}

// SDK 로드 상태
type SDKStatus = 'idle' | 'loading' | 'loaded' | 'error';

class MobileOKService {
  private sdkStatus: SDKStatus = 'idle';
  private sdkLoadPromise: Promise<void> | null = null;

  // 개발/운영 환경 구분
  private readonly isDev =
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost';

  // SDK URL (개발/운영)
  private readonly SDK_URL = this.isDev
    ? 'https://scert.mobile-ok.com/resources/js/index.js'
    : 'https://cert.mobile-ok.com/resources/js/index.js';

  /**
   * Mobile-OK SDK 스크립트 로드
   */
  loadScript(): Promise<void> {
    if (this.sdkStatus === 'loaded') {
      return Promise.resolve();
    }

    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    this.sdkStatus = 'loading';
    this.sdkLoadPromise = new Promise((resolve, reject) => {
      // 이미 로드된 경우
      if (window.MOBILEOK) {
        this.sdkStatus = 'loaded';
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.SDK_URL;
      script.async = true;

      script.onload = () => {
        this.sdkStatus = 'loaded';
        console.log('[MobileOK] SDK loaded successfully');
        resolve();
      };

      script.onerror = () => {
        this.sdkStatus = 'error';
        this.sdkLoadPromise = null;
        const error = new Error('[MobileOK] Failed to load SDK');
        console.error(error);
        reject(error);
      };

      document.head.appendChild(script);
    });

    return this.sdkLoadPromise;
  }

  /**
   * 본인확인 표준창 실행
   * @param requestUrl 백엔드 요청 URL
   * @param deviceType 디바이스 타입 (WB=PC웹, MB=모바일웹)
   * @param callbackFuncName 콜백 함수명 (문자열)
   */
  async process(
    requestUrl: string,
    deviceType: string = 'WB',
    callbackFuncName: string = '',
  ): Promise<void> {
    await this.loadScript();

    if (!window.MOBILEOK) {
      throw new Error('[MobileOK] SDK not available');
    }

    console.log('[MobileOK] Calling MOBILEOK.process with:', {
      requestUrl,
      deviceType,
      callbackFuncName,
    });

    window.MOBILEOK.process(requestUrl, deviceType, callbackFuncName);
  }

  /**
   * SDK 로드 상태 확인
   */
  isLoaded(): boolean {
    return this.sdkStatus === 'loaded' && !!window.MOBILEOK;
  }

  /**
   * 현재 환경 확인 (개발/운영)
   */
  getEnvironment(): 'development' | 'production' {
    return this.isDev ? 'development' : 'production';
  }

  /**
   * 클라이언트 txId 생성
   * 형식: MOK_{userId}_{timestamp}_{random}
   */
  private generateClientTxId(userId: number): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `MOK_${userId}_${timestamp}_${random}`;
  }

  /**
   * 디바이스 타입 감지
   * WB: PC 웹, MB: 모바일 웹
   */
  detectDeviceType(): 'WB' | 'MB' {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    return isMobile ? 'MB' : 'WB';
  }

  /**
   * 휴대폰 인증 시작
   *
   * Mobile-OK는 EZ-IOK와 달리 반드시 returnUrl이 필요합니다.
   * SDK가 인증 완료 후 returnUrl로 mokToken을 POST합니다.
   *
   * 흐름:
   * 1. 프론트에서 clientTxId 생성 → sessionStorage 저장
   * 2. SDK 호출 (백엔드 requestUrl에 clientTxId + returnUrl 포함)
   * 3. SDK → returnUrl로 mokToken POST (프론트 콜백 페이지)
   * 4. 프론트 콜백 페이지 → 백엔드 /result API 호출 (mokToken + txId)
   * 5. 프론트 콜백 페이지 → opener에 postMessage로 결과 전달
   */
  async startAuth(params: MobileOKRequestParams): Promise<void> {
    const userId = params.userId || 0;

    // 프론트엔드에서 txId를 먼저 생성
    const clientTxId = this.generateClientTxId(userId);

    // sessionStorage에 txId 저장 (결과 조회 시 사용)
    sessionStorage.setItem('mobileok_txId', clientTxId);
    console.log('[MobileOK] Generated clientTxId:', clientTxId);

    // returnUrl: 백엔드 콜백 URL
    // Mobile-OK SDK는 POST 방식으로 mokToken을 전달하므로,
    // 백엔드에서 POST를 받고 → 프론트엔드로 GET 리다이렉트해야 함
    //
    // 흐름:
    // 1. SDK → 백엔드 /func/mobileok/callback (POST)
    // 2. 백엔드 → 프론트엔드 /auth/mobileok/callback (GET 리다이렉트)
    const returnUrl = `${API_BASE}/func/mobileok/callback`;

    // localStorage에서 인증 토큰 가져오기
    // SDK가 직접 URL을 호출하므로 axiosClient 인터셉터가 적용되지 않음
    const accessToken = localStorage.getItem('token');

    // 백엔드 요청 URL 구성
    const queryParams = new URLSearchParams({
      usageCode: params.usageCode,
      serviceType: params.serviceType || 'telcoAuth',
      clientTxId: clientTxId,
      returnUrl: returnUrl,
    });

    // 인증 토큰이 있으면 쿼리 파라미터로 추가
    if (accessToken) {
      queryParams.append('access_token', accessToken);
    }

    const requestUrl = `${API_BASE}/func/mobileok/request?${queryParams.toString()}`;
    const deviceType = this.detectDeviceType();

    console.log('[MobileOK] Starting auth with:', { requestUrl, deviceType, clientTxId, returnUrl });

    await this.process(requestUrl, deviceType, 'mobileOKCallback');
  }
}

export default new MobileOKService();